import { createRootRoute, Outlet, redirect } from '@tanstack/react-router'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Notifications } from '@mantine/notifications'
import NewLayout from '@/PageComponents/Layout/Layout'
import ToastContext from '@/utils/toast-context'
import PageContext from '@/utils/page-context'
import Constants from '@/utils/constants'
import SCMessageBarContext from '@/utils/contexts/sc-message-bar-context'
import SCMessageBar from '@/components/sc-controls/message-bar/sc-message-bar'
import SubscriptionContext from '@/utils/subscription-context'
import BillingService from '@/services/billing-service'
import Storage from '@/utils/storage'
import * as Enums from '@/utils/enums'
import { colors, layout } from '@/theme'
import TimerContext from '@/utils/timer-context'
import useRefState from '@/hooks/useRefState'
import jobTimerService from '@/services/job/job-timer-service'
import integrationService from '@/services/integration-service'
import ConfettiOverlay from '@/components/confetti/ConfettiOverlay'

const MAINTENANCE_URL_BASE = 'https://scgeneraltechstorage.blob.core.windows.net/maintenance/<host>.json'

// Defined outside the component — reads only from Storage, no React state dependency.
function autoDismissMaintenanceSchedule(schedule: any) {
  if (schedule?.showMaintenanceWarning) {
    const dismissedId = Storage.getCookie(Enums.Cookie.maintenanceMessageDismissed)
    if (dismissedId === schedule.id) {
      schedule.showMaintenanceWarning = false
    }
  }
  return schedule
}

export const Route = createRootRoute({
  beforeLoad: ({ location }) => {
    const path = location.pathname.toLowerCase()
    const isPublic =
      Constants.PUBLIC_ROUTES_EXACT.has(path) ||
      Constants.PUBLIC_ROUTE_PREFIXES.some((p: string) => path.startsWith(p))

    if (!isPublic && !Storage.getCookie(Enums.Cookie.token)) {
      throw redirect({ to: '/login', search: { redirect: location.href } })
    }
  },
  component: RootLayout,
})

function RootLayout() {
  const [toast, setToast] = useState<any>({ show: false, message: '' })
  const [tenantName, setTenantName] = useState('')
  const [mobileSidebarExpanded, setMobileSidebarExpanded] = useState(false)
  const [sideBarStateChanged, setSideBarStateChanged] = useState(0)
  const updateTenantName = (newName: any) => {
    if (newName !== tenantName) setTenantName(newName)
  }
  const [messageBarIsActive, setMessageBarIsActive] = useState(false)
  const [messageBarType, setMessageBarType] = useState<any>(null)
  const [messageBarMessage, setMessageBarMessage] = useState<any>(null)
  const [integrationErrorActive, setIntegrationErrorActive] = useState(false)
  const [integrationErrorMessage, setIntegrationErrorMessage] = useState<string | null>(null)
  const integrationErrorDismissedRef = useRef(false)
  const [subscriptionInfo, setSubscriptionInfo] = useState<any>(Storage.getCookie(Enums.Cookie.subscriptionInfo))
  const [currentMaintenanceSchedule, setCurrentMaintenanceSchedule] = useState<any>(null)
  const currentMaintenanceScheduleRef = useRef<any>(null)
  const reloadTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const mouseRef = useRef({ x: -1, y: -1, stamp: new Date().valueOf() })
  const runningTimerIntervalRef = useRef<any>(null)
  const [runningTimers, setRunningTimers, getRunningTimersValue] = useRefState<any[]>([])

  const updateRunningTimers = async (force = false) => {
    const hasEmployee = Storage.hasCookieValue(Enums.Cookie.employeeID)
    const isInactive = new Date().valueOf() > mouseRef.current.stamp + 60_000
    if (!hasEmployee || isInactive || currentMaintenanceScheduleRef.current?.showMaintenanceMessage) return
    const timersResult = await jobTimerService.getRunningTimers(force)
    if (JSON.stringify(timersResult) !== JSON.stringify(getRunningTimersValue())) {
      setRunningTimers(timersResult as any)
    }
  }

  const clearRunningTimers = () => setRunningTimers([] as any)

  useEffect(() => {
    const onMouseMove = (ev: MouseEvent) => {
      if (mouseRef.current.x !== ev.clientX || mouseRef.current.y !== ev.clientY) {
        mouseRef.current = { x: ev.clientX, y: ev.clientY, stamp: new Date().valueOf() }
      }
    }
    window.addEventListener('mousemove', onMouseMove)
    updateRunningTimers(true)
    runningTimerIntervalRef.current = setInterval(() => updateRunningTimers(false), 10_000)
    return () => {
      window.removeEventListener('mousemove', onMouseMove)
      clearInterval(runningTimerIntervalRef.current)
    }
  }, [])

  const getSubscriptionInfo = async (invalidate = false) => {
    if (!subscriptionInfo) return
    const [subscriptionInfoResult] = await BillingService.getSubcriptionInfo(null, invalidate)
    setSubscriptionInfo(subscriptionInfoResult)
    return subscriptionInfoResult
  }

  const dismissMaintenanceSchedule = () => {
    const id = currentMaintenanceScheduleRef.current?.id
    if (!id) return
    Storage.setCookie(Enums.Cookie.maintenanceMessageDismissed, id)
    const updated = { ...currentMaintenanceScheduleRef.current, showMaintenanceWarning: false }
    currentMaintenanceScheduleRef.current = updated
    setCurrentMaintenanceSchedule(updated)
  }

  const checkVersion = useCallback(async () => {
    try {
      const res = await fetch('/meta.json', { cache: 'no-cache' })
      const { version } = await res.json()
      if (version && version !== Constants.appVersion()) {
        if (reloadTimerRef.current) return
        reloadTimerRef.current = setTimeout(async () => {
          const cacheNames = await caches.keys()
          await Promise.all(cacheNames.map(name => caches.delete(name)))
          window.location.reload()
        }, Constants.reloadVersionDebouncePeriodMS ?? 5000)
      }
    } catch { /* ignore network errors */ }
  }, [])

  const applyIntegrationResult = useCallback((integration: any) => {
    if (!integration) {
      integrationErrorDismissedRef.current = false
      setIntegrationErrorActive(false)
      setIntegrationErrorMessage(null)
    } else if (!integrationErrorDismissedRef.current) {
      setIntegrationErrorActive(true)
      setIntegrationErrorMessage(integration.IsLive ? integration.ErrorMessage ?? null : null)
    }
  }, [])


  const checkMaintenance = useCallback(async () => {
    try {
      let host = window.location.host
      const portIdx = host.indexOf(':')
      if (portIdx > -1) host = host.substring(0, portIdx)
      const url = `${MAINTENANCE_URL_BASE.replace('<host>', host)}?_=${Math.random()}`
      const res = await fetch(url)
      const { maintenanceSchedule: schedule } = await res.json()
      const processed = autoDismissMaintenanceSchedule(schedule)
      if (JSON.stringify(processed) !== JSON.stringify(currentMaintenanceScheduleRef.current)) {
        currentMaintenanceScheduleRef.current = processed
        setCurrentMaintenanceSchedule(processed)
      }
    } catch { /* ignore — maintenance blob may not exist for this host */ }
  }, [])

  useEffect(() => {
    checkVersion()
    checkMaintenance()
    const interval = setInterval(() => {
      checkVersion()
      checkMaintenance()
    }, 20_000)
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkVersion()
        checkMaintenance()
      }
    }
    document.addEventListener('visibilitychange', onVisibilityChange)
    return () => {
      clearInterval(interval)
      document.removeEventListener('visibilitychange', onVisibilityChange)
    }
  }, [checkVersion, checkMaintenance])

  useEffect(() => {
    if (!Storage.hasCookieValue(Enums.Cookie.employeeID)) return
    return integrationService.startIntegrationPolling(applyIntegrationResult)
  }, [applyIntegrationResult])

  if (currentMaintenanceSchedule?.showMaintenanceMessage) {
    return (
      <div style={{ display: 'flex', margin: 0, padding: 0, height: '100vh' }}>
        <div style={{ display: 'flex', flexDirection: 'column', flexGrow: 1, overflow: 'hidden' }}>
          <div style={{
            backgroundColor: colors.background,
            borderTopLeftRadius: layout.bodyRadius,
            flexGrow: 1,
            overflowY: 'auto',
            padding: '1rem',
            textAlign: 'center',
            position: 'relative',
          }}>
            <img src="/server-maintenance.svg" style={{ position: 'fixed', right: '0.5rem', bottom: '0.5rem', opacity: 0.2, height: '4rem' }} alt="" />
            <div style={{ marginTop: '50vh', transform: 'translateY(-50%)' }}>
              <img src="/logo-type-blue.svg" alt="ServCraft" />
              <h4>{currentMaintenanceSchedule.maintenanceMessage}</h4>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <TimerContext.Provider value={{ runningTimers, updateRunningTimers, clearRunningTimers }}>
    <SubscriptionContext.Provider value={{ subscriptionInfo, setSubscriptionInfo, getSubscriptionInfo }}>
      <SCMessageBarContext.Provider value={{
        isActive: messageBarIsActive || !!currentMaintenanceSchedule?.showMaintenanceWarning,
        setIsActive: setMessageBarIsActive,
        setMessageBarType,
        setMessage: setMessageBarMessage,
        integrationErrorActive,
        setIntegrationErrorActive,
        integrationErrorMessage,
        maintenanceActive: !!currentMaintenanceSchedule?.showMaintenanceWarning,
      }}>
        <PageContext.Provider value={{
          tenantName, updateTenantName,
          mobileSidebarExpanded, setMobileSidebarExpanded,
          sideBarStateChanged, setSideBarStateChanged,
          customerZone: false, tenantZone: false,
          setTimeshiftMilliseconds: () => {},
        }}>
          <ToastContext.Provider value={{ toast, setToast }}>
            <NewLayout>
              <Notifications position="top-center" />
              <Outlet />
            </NewLayout>
            <SCMessageBar
              messageBarType={messageBarType}
              message={messageBarMessage}
              isActive={messageBarIsActive}
              maintenanceSchedule={currentMaintenanceSchedule}
              dismissMaintenanceSchedule={dismissMaintenanceSchedule}
              integrationErrorActive={integrationErrorActive}
              integrationErrorMessage={integrationErrorMessage}
              onDismissIntegrationError={() => {
                integrationErrorDismissedRef.current = true
                setIntegrationErrorActive(false)
              }}
            />
            <ConfettiOverlay />
          </ToastContext.Provider>
        </PageContext.Provider>
      </SCMessageBarContext.Provider>
    </SubscriptionContext.Provider>
    </TimerContext.Provider>
  )
}
